package com.arena_web.ui.theme

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.arena_web.ui.utils.sFontSize
import com.arena_web.ui.utils.sLetterSpacing
import com.arena_web.ui.utils.sLineHeight

/**
 * Typography system based on Figma design tokens.
 *
 * Fonts:
 *  - Urbanist: primary (headings + body)
 *  - SF Pro Display: card headlines, small bold (system fallback)
 *  - IBM Plex Sans: card text
 */
object AppTypography {

    // -- Font families --
    const val PRIMARY_FONT_NAME = "Urbanist"
    const val PRIMARY_FONT_FALLBACK = "sans-serif"

    // Urbanist is also the default for section titles / logo — keep SECONDARY pointing to it
    const val SECONDARY_FONT_NAME = "Urbanist"
    const val SECONDARY_FONT_FALLBACK = "sans-serif"

    // Card headline font (Apple system font stack)
    const val CARD_HEADLINE_FONT = "SF Pro Display"
    val CARD_HEADLINE_FALLBACKS = arrayOf("-apple-system", "BlinkMacSystemFont", "sans-serif")

    // Card text font
    const val CARD_TEXT_FONT = "IBM Plex Sans"
    const val CARD_TEXT_FALLBACK = "sans-serif"

    // -- Font weights --
    val Light = 300
    val Regular = 400
    val Medium = 500
    val SemiBold = 600
    val Bold = 700
}

// =============================================================================
// Heading styles
// =============================================================================

/** H0 — Urbanist SemiBold 46/46, ls 1px */
fun Modifier.h0(): Modifier = this
    .sFontSize(46)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(46)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** H1 — Urbanist SemiBold 34/40, ls 1px */
fun Modifier.h1(): Modifier = this
    .sFontSize(34)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(40)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** H2 — Urbanist SemiBold 26/30, ls 1px */
fun Modifier.h2(): Modifier = this
    .sFontSize(26)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(30)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** H2 Medium — Urbanist Medium 24/32, ls 1px */
fun Modifier.h2Medium(): Modifier = this
    .sFontSize(24)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(32)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** H3 — Urbanist Medium 20/24, ls 1px */
fun Modifier.h3(): Modifier = this
    .sFontSize(20)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(24)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

// =============================================================================
// Body 3XL
// =============================================================================

/** Body/3XL/S — Urbanist SemiBold 36/42, ls 1px */
fun Modifier.body3xlSemiBold(): Modifier = this
    .sFontSize(36)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(42)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

// =============================================================================
// Body 2XL
// =============================================================================

/** Body/2XL/S — Urbanist SemiBold 22/26, ls 1px */
fun Modifier.body2xlSemiBold(): Modifier = this
    .sFontSize(22)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(26)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** Body/2XL/M — Urbanist Medium 22/26, ls 1px */
fun Modifier.body2xlMedium(): Modifier = this
    .sFontSize(22)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(26)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** Body/2XL/R — Urbanist Regular 22/26, ls 1px */
fun Modifier.body2xlRegular(): Modifier = this
    .sFontSize(22)
    .fontWeight(AppTypography.Regular)
    .sLineHeight(26)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

// =============================================================================
// Body XL
// =============================================================================

/** Body/XL/M — Urbanist Medium 18/22, ls 0.5px */
fun Modifier.bodyXlMedium(): Modifier = this
    .sFontSize(18)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(22)
    .sLetterSpacing(0.5)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** Body/XL/S — Urbanist SemiBold 18/22, ls 0.5px */
fun Modifier.bodyXlSemiBold(): Modifier = this
    .sFontSize(18)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(22)
    .sLetterSpacing(0.5)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** Body/XL/B — Urbanist Bold 18/22, ls 0.5px */
fun Modifier.bodyXlBold(): Modifier = this
    .sFontSize(18)
    .fontWeight(AppTypography.Bold)
    .sLineHeight(22)
    .sLetterSpacing(0.5)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

// =============================================================================
// Body L
// =============================================================================

/** Body/L/M — Urbanist Medium 16/18, ls 0.2px */
fun Modifier.bodyLMedium(): Modifier = this
    .sFontSize(16)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(18)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** Body/L/R — Urbanist Regular 16/16, ls 0.2px */
fun Modifier.bodyLRegular(): Modifier = this
    .sFontSize(16)
    .fontWeight(AppTypography.Regular)
    .sLineHeight(16)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** Body/L/L — Urbanist Light 16/18, ls 0.2px */
fun Modifier.bodyLLight(): Modifier = this
    .sFontSize(16)
    .fontWeight(AppTypography.Light)
    .sLineHeight(18)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

// =============================================================================
// Body M
// =============================================================================

/** Body/M/R — Urbanist Regular 14/14, ls 0.2px */
fun Modifier.bodyMRegular(): Modifier = this
    .sFontSize(14)
    .fontWeight(AppTypography.Regular)
    .sLineHeight(14)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

// =============================================================================
// Body S
// =============================================================================

/** Body/S/R — Urbanist Regular 12/12, ls 0.1px */
fun Modifier.bodySRegular(): Modifier = this
    .sFontSize(12)
    .fontWeight(AppTypography.Regular)
    .sLineHeight(12)
    .sLetterSpacing(0.1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** Body/S/M — Urbanist Medium 12/12, ls 0.1px */
fun Modifier.bodySMedium(): Modifier = this
    .sFontSize(12)
    .fontWeight(AppTypography.Medium)
    .sLineHeight(12)
    .sLetterSpacing(0.1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** Body/S/B — SF Pro Display Bold 12/12, ls 0.1px */
fun Modifier.bodySBold(): Modifier = this
    .sFontSize(12)
    .fontWeight(AppTypography.Bold)
    .sLineHeight(12)
    .sLetterSpacing(0.1)
    .fontFamily(AppTypography.CARD_HEADLINE_FONT, *AppTypography.CARD_HEADLINE_FALLBACKS)

// =============================================================================
// Card styles (SF Pro Display / IBM Plex Sans)
// =============================================================================

/** Body/card/headline — SF Pro Display Bold 22/26, ls 1px */
fun Modifier.cardHeadline(): Modifier = this
    .sFontSize(22)
    .fontWeight(AppTypography.Bold)
    .sLineHeight(26)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.CARD_HEADLINE_FONT, *AppTypography.CARD_HEADLINE_FALLBACKS)

/** Body/card/text_M — IBM Plex Sans SemiBold 16/18, ls 0.2px */
fun Modifier.cardTextMedium(): Modifier = this
    .sFontSize(16)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(18)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.CARD_TEXT_FONT, AppTypography.CARD_TEXT_FALLBACK)

/** Body/card/text_S — IBM Plex Sans SemiBold 14/14, ls 0.2px */
fun Modifier.cardTextSmall(): Modifier = this
    .sFontSize(14)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(14)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.CARD_TEXT_FONT, AppTypography.CARD_TEXT_FALLBACK)

// =============================================================================
// Business / Admin body styles (from DesignSystem_Arena-Admin_Font)
// =============================================================================

/** Body/XL/R (Business) — Urbanist Regular 18/18, ls 0.5px */
fun Modifier.bodyXlRegular(): Modifier = this
    .sFontSize(18)
    .fontWeight(AppTypography.Regular)
    .sLineHeight(18)
    .sLetterSpacing(0.5)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** Body/L/S (Business) — Urbanist SemiBold 16/18, ls 0.2px */
fun Modifier.bodyLSemiBold(): Modifier = this
    .sFontSize(16)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(18)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** Body/M/L (Business) — Urbanist Light 14/14, ls 0.2px */
fun Modifier.bodyMLight(): Modifier = this
    .sFontSize(14)
    .fontWeight(AppTypography.Light)
    .sLineHeight(14)
    .sLetterSpacing(0.2)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** Body/XL/S (Business) — Urbanist SemiBold 20/26, ls 1px */
fun Modifier.businessBodyXlSemiBold(): Modifier = this
    .sFontSize(20)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(26)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

/** Body/XL/M (Business) — Urbanist SemiBold 20/24, ls 1px */
fun Modifier.businessBodyXlMedium(): Modifier = this
    .sFontSize(20)
    .fontWeight(AppTypography.SemiBold)
    .sLineHeight(24)
    .sLetterSpacing(1)
    .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)

// =============================================================================
// Legacy aliases (for backward compatibility with existing code)
// =============================================================================

fun Modifier.h4(): Modifier = h2Medium()
fun Modifier.body(): Modifier = bodyLRegular()
fun Modifier.bodySmall(): Modifier = bodyMRegular()
fun Modifier.caption(): Modifier = bodySRegular()
